<template>
  <div class="product-color-picker__wrapper">
    <slot :items="items">
      <h2
        v-if="!fromListingPage"
        class="product-color-picker__selected-color-name"
      >
        {{ $t('pdp.color') }}: <strong>{{ selectedColorName }}</strong>
      </h2>
      <div
        class="product-color-picker"
        :class="{
          'product-color-picker--listing-page': fromListingPage,
          'product-color-picker--detail-page': !fromListingPage,
        }"
        data-test-id="product-color-picker"
      >
        <slot
          v-for="item in itemsToShow"
          :key="item?.id"
          name="item"
          :item="item"
        >
        </slot>
        <slot name="further-items" :count="furtherItemsCount">
          <span
            v-show="
              showLimit &&
              furtherItemsCount > 0 &&
              (!isPdp || hideMoreColorsLabel)
            "
            class="product-color-picker__limit"
            :class="{
              'product-color-picker__limit--listing-page': fromListingPage,
            }"
            @click="toggleShowLimit"
            >+ {{ furtherItemsCount }}</span
          >
          <Button
            v-show="
              isPdp &&
              showLimit &&
              furtherItemsCount > 0 &&
              !hideMoreColorsLabel
            "
            type="ghost"
            no-padding
            ignore-capitalisation
            class="flex cursor-pointer items-center"
            @click="toggleShowLimit"
          >
            {{ $t('pdp.more_colors') }}
            <IconPlus class="size-4" />
          </Button>
        </slot>
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type { ProductSibling } from '@scayle/storefront-nuxt'
import useExperiments from '~/composables/useExperiments'
import { orderSiblingColorsBySelected } from '~/utils/product'

interface ProductColor {
  id: number
  label: string
  value: string
}

export interface ProductColorItem {
  id: number
  colors: ProductColor[]
}

const props = defineProps({
  items: {
    type: Array as PropType<ProductSibling[]>,
    default: () => [],
  },
  selectedColorName: {
    type: String as PropType<string>,
    default: '',
  },
  fromListingPage: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isFoldable: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  hideMoreColorsLabel: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  selectedId: {
    type: Number as PropType<number>,
    default: undefined,
  },
})

const { showPdpFoldableColors } = useExperiments()
const { isPdp } = useUiState()
const limit = isPdp ? 4 : 5
const showLimit = ref(props.fromListingPage || props.isFoldable)
const orderedItems = computed(() => {
  // PDP foldable experiment surfaces the selected colour (by name) first.
  if (isPdp && showPdpFoldableColors.value) {
    const itemsCopy = props.items.slice()
    const selectedItemIndex = itemsCopy.findIndex((item) =>
      item.colors.some((color) => color.label === props.selectedColorName),
    )

    if (selectedItemIndex !== -1) {
      const [selectedItem] = itemsCopy.splice(selectedItemIndex, 1)
      if (selectedItem) {
        itemsCopy.unshift(selectedItem)
      }
    }

    return itemsCopy
  }

  // Listing page (and default): sort by id, then surface the swatch matching
  // the displayed product image first. See ECOM-2642 / ECOM-2644.
  return orderSiblingColorsBySelected(props.items, props.selectedId)
})

const itemsToShow = computed(() => {
  const items = showLimit.value
    ? orderedItems.value.slice(0, limit)
    : orderedItems.value
  return items.filter(Boolean)
})
const furtherItemsCount = computed(() => orderedItems.value.length - limit)

const toggleShowLimit = () => {
  showLimit.value = !showLimit.value
}
</script>

<style lang="postcss" scoped>
.product-color-picker__selected-color-name {
  @apply text-base
    font-normal
    leading-[19px]
    mb-[6px]
    md:mb-[8px];
}
.product-color-picker {
  @apply flex
  flex-wrap;
}
.product-color-picker--listing-page {
  @apply lg:gap-[2px];
}
.product-color-picker--detail-page {
  @apply gap-[14px]
  md:gap-[9px];
}
.product-color-picker--detail-page :deep(a),
.product-color-picker--detail-page :deep(button) {
  @apply inline-flex
    min-h-8
    min-w-8
    items-center
    justify-center;
}
.product-color-picker__limit {
  @apply pl-1
  cursor-pointer
  font-medium
  leading-none;
}
.product-color-picker__limit--listing-page {
  /* Match ColorCircle's listing-page box (h-[11px] pb-0.5 lg:h-5 lg:pb-1) and
     centre the label inside it. Without this the label is positioned by its
     own line box, which sits at the top of the stretched flex item and lands
     ~2px below the swatch centres — and the exact offset depends on the
     font's metrics rather than on the layout. */
  @apply flex
  h-[11px]
  items-center
  pb-0.5
  text-xs
  lg:h-5
  lg:pb-1
  lg:text-base;
}
.product-color-picker__names {
  @apply mt-4
    font-light
    text-xs;
}
</style>
